import { forwardRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

import CertificateSubtypes from 'todistukset-common/src/vtj/CertificateSubtypes.enum'

import { Heading, Block, RouterLink, ExternalLink } from 'suomifi-ui-components'
import { IconPdf } from 'suomifi-icons'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'

import { ParagraphWithNewlines } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import {
  FormContainer,
  StyledLinkList,
  StyledLinkListItem,
  StyledLoadingSpinner,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderCompleteStep.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'

import {
  PreviousOrder,
  getOrder,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderCompleteStep.util'
import ReceiptDownload from 'todistukset-ui/src/vtj/components/order-wizard/ReceiptDownload.react'
import OrderCompleteHeading from 'todistukset-ui/src/vtj/components/order-wizard/OrderCompleteHeading.react'
import { useDocumentDownloadStatus } from 'todistukset-ui/src/vtj/hooks'
import { getSubjectPersons } from 'todistukset-common/src/vtj/todistus-person.util'
import { getVtjOteTodistusAsiakirjaName } from 'todistukset-common/src/vtj/asiakirja.util'

const OrderCompleteStep: StepComponent = observer(
  forwardRef(function OrderCompleteStep({ titleId, certificateType }, _ref) {
    const { paymentId } = useParams()
    const store = useStore()
    const {
      common: { lang },
    } = store

    const order = getOrder(store, paymentId)
    if (!order || order.form.todistusType !== certificateType) {
      return null
    }

    const isElectronic =
      order.form.todistusSubtype === CertificateSubtypes.SAHKOINEN

    return (
      <FormContainer>
        <Block variant="section">
          <OrderCompleteHeading
            titleId={titleId}
            headingTranslationKey="orderWizardOrderCompleteStepMainTitle"
          />
          <ParagraphWithNewlines>
            <LocalizedString
              stringKey={
                isElectronic
                  ? 'orderWizardOrderCompleteStepMainDescriptionElectronic_suomifi_disabled'
                  : 'orderWizardOrderCompleteStepMainDescriptionMail'
              }
            />
          </ParagraphWithNewlines>
        </Block>
        {isElectronic && (
          <Block variant="section">
            <Heading variant="h3">
              <LocalizedString stringKey="orderWizardOrderCompleteStepElectronicDownloadsTitle" />
            </Heading>
            <TodistusDownloadLinks order={order} />
          </Block>
        )}
        <ReceiptDownload />
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardOrderCompleteStepFinallyTitle" />
          </Heading>
          <ParagraphWithNewlines>
            <LocalizedString
              stringKey={
                isElectronic
                  ? 'orderWizardOrderCompleteStepFinallyDescriptionElectronic'
                  : 'orderWizardOrderCompleteStepFinallyDescriptionMail'
              }
            />
          </ParagraphWithNewlines>
          <ParagraphWithNewlines>
            <RouterLink
              asComponent={Link}
              to={`/${lang}#top`}
              underline="initial"
            >
              <LocalizedString stringKey="orderWizardReturnToFrontPageLinkText" />
            </RouterLink>
          </ParagraphWithNewlines>
        </Block>
      </FormContainer>
    )
  })
)

const TodistusDownloadLinks = observer(
  ({ order }: { order: PreviousOrder }) => {
    const { t } = useTranslation()
    const { isDownloadable, isPolling, pollingTimedOut } =
      useDocumentDownloadStatus(order.form.requestId)

    const subjects = getSubjectPersons(order.form.persons)

    return (
      <>
        {(isPolling || pollingTimedOut) && (
          <StyledLoadingSpinner
            text={
              pollingTimedOut
                ? t('documentCreationTimedOut')
                : t('documentsBeingCreated')
            }
            status={pollingTimedOut ? 'failed' : 'loading'}
            textAlign="right"
          />
        )}

        {isDownloadable && (
          <StyledLinkList ariaLabelledBy="orderWizardOrderCompleteStepElectronicDownloadsTitle">
            {subjects.map((subject) => {
              const filename = getVtjOteTodistusAsiakirjaName({
                person: subject,
                todistusLanguage: order.form.todistusLanguage,
                todistusType: order.form.todistusType,
              })

              return (
                <StyledLinkListItem key={subject.personId} icon={<IconPdf />}>
                  <ExternalLink
                    data-test-id="certificate-download-link"
                    href={`/api/restricted/todistukset/vtj-download/${
                      order.form.requestId
                    }/${subject.personId}?filename=${encodeURIComponent(
                      filename
                    )}`}
                    labelNewWindow={t('opensInNewWindow')}
                    toNewWindow
                  >
                    {filename}
                  </ExternalLink>
                </StyledLinkListItem>
              )
            })}
          </StyledLinkList>
        )}
      </>
    )
  }
)

export default OrderCompleteStep
